import { createFileRoute } from "@tanstack/react-router";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Download,
  Search,
  Send,
  Bell,
  UserX,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import type { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import {
  adminApi,
  type LaunchSubscriberRow,
  type LaunchSubscriberStatus,
  type ListLaunchSubscribersInput,
} from "@/lib/api";
import { LIST_STALE_MS, queryKeys } from "@/lib/queries";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { launchSubscriberTone } from "@/lib/status-tone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/DataTable";
import { StatusPill } from "@/components/StatusPill";
import { downloadCsv } from "@/lib/csv";
import { queryTableProps } from "@/lib/data-table";

export const Route = createFileRoute("/_authed/launch-subscribers")({
  component: SubscribersPage,
});

const STATUSES = ["", "pending", "notified", "unsubscribed"] as const;
const PAGE_SIZE = 50;

function SubscribersPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  // Typed filters key a server query: settle them before fetching.
  const debouncedSearch = useDebouncedValue(search);
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("");
  const [selection, setSelection] = useState<RowSelectionState>({});
  const [busy, setBusy] = useState<false | "launch" | "reminder" | "unsub">(false);
  const [reminderMsg, setReminderMsg] = useState("");
  const [reminderOpen, setReminderOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const filters: ListLaunchSubscribersInput = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      status: status ? (status as LaunchSubscriberStatus) : undefined,
      limit: PAGE_SIZE,
    }),
    [debouncedSearch, status],
  );

  const query = useInfiniteQuery({
    queryKey: queryKeys.launchSubscribers.list(filters),
    queryFn: async ({ pageParam }) => {
      const res = await adminApi.listLaunchSubscribers()({
        ...filters,
        cursor: pageParam ?? null,
      });
      return res.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: LIST_STALE_MS,
  });

  const rows = query.data?.pages.flatMap((p) => p.subscribers) ?? [];
  const totals = query.data?.pages[0]?.totals;

  // A filter/search change can hide rows an operator previously selected —
  // clear the selection so bulk actions can't silently act on rows that are
  // no longer visible.
  useEffect(() => {
    setSelection({});
  }, [filters]);

  const selectedIds = useMemo(
    () => Object.keys(selection).filter((k) => selection[k]),
    [selection],
  );

  // Selected ids that are still "pending" — only those are eligible for
  // launch / reminder actions. The CFs already enforce this server-side, but
  // the UI button state should match.
  const eligibleSelectedIds = useMemo(
    () => rows.filter((r) => selection[r.id] && r.status === "pending").map((r) => r.id),
    [rows, selection],
  );

  const columns = useMemo<ColumnDef<LaunchSubscriberRow, unknown>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <input
            type="checkbox"
            aria-label="Select all"
            checked={table.getIsAllPageRowsSelected()}
            onChange={(e) => table.toggleAllPageRowsSelected(!!e.target.checked)}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            aria-label="Select row"
            checked={row.getIsSelected()}
            onChange={(e) => row.toggleSelected(!!e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        ),
        enableSorting: false,
      },
      {
        header: "Email",
        accessorKey: "email",
        cell: ({ row }) => <span className="font-medium">{row.original.email}</span>,
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => (
          <StatusPill tone={launchSubscriberTone(row.original.status)}>{row.original.status}</StatusPill>
        ),
      },
      {
        header: "Source",
        accessorKey: "source",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">{row.original.source}</span>
        ),
      },
      {
        header: "Subscribed",
        accessorKey: "subscribedAt",
        cell: ({ row }) => fmtDate(row.original.subscribedAt),
      },
      {
        header: "Notified",
        accessorKey: "notifiedAt",
        cell: ({ row }) => fmtDate(row.original.notifiedAt),
      },
      {
        header: "Reminder",
        accessorKey: "reminderSentAt",
        cell: ({ row }) => fmtDate(row.original.reminderSentAt),
      },
    ],
    [],
  );

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.launchSubscribers.all });
  }

  async function handleSendLaunch() {
    if (eligibleSelectedIds.length === 0) return;
    if (
      !window.confirm(
        `Send the launch announcement to ${eligibleSelectedIds.length} pending subscriber(s)? ` +
          `This sets their status to "notified" and is one-shot.`,
      )
    ) {
      return;
    }
    setBusy("launch");
    setError(null);
    setInfo(null);
    try {
      const res = await adminApi.sendLaunchEmail()({ subscriberIds: eligibleSelectedIds });
      setInfo(
        `Launch email — sent: ${res.data.sent}, skipped: ${res.data.skipped}, failed: ${res.data.failed}`,
      );
      setSelection({});
      refresh();
    } catch (e) {
      setError((e as Error).message ?? "Send failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleSendReminder() {
    if (eligibleSelectedIds.length === 0) return;
    setBusy("reminder");
    setError(null);
    setInfo(null);
    try {
      const res = await adminApi.sendReminderEmail()({
        subscriberIds: eligibleSelectedIds,
        customMessage: reminderMsg.trim() || undefined,
      });
      setInfo(
        `Reminder — sent: ${res.data.sent}, skipped: ${res.data.skipped}, failed: ${res.data.failed}`,
      );
      setSelection({});
      setReminderOpen(false);
      setReminderMsg("");
      refresh();
    } catch (e) {
      setError((e as Error).message ?? "Send failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleUnsubscribe() {
    if (selectedIds.length === 0) return;
    if (
      !window.confirm(
        `Mark ${selectedIds.length} subscriber(s) as unsubscribed? They will be excluded from future launch and reminder sends.`,
      )
    ) {
      return;
    }
    setBusy("unsub");
    setError(null);
    setInfo(null);
    try {
      const res = await adminApi.markUnsubscribed()({ subscriberIds: selectedIds });
      setInfo(`Unsubscribed: ${res.data.updated}, skipped: ${res.data.skipped}`);
      setSelection({});
      refresh();
    } catch (e) {
      setError((e as Error).message ?? "Mark failed");
    } finally {
      setBusy(false);
    }
  }

  function exportCsv() {
    downloadCsv(
      `launch-subscribers-${format(new Date(), "yyyyMMdd-HHmmss")}.csv`,
      rows.map((r) => ({
        id: r.id,
        email: r.email,
        status: r.status,
        source: r.source,
        subscribedAt: r.subscribedAt ? new Date(r.subscribedAt).toISOString() : "",
        notifiedAt: r.notifiedAt ? new Date(r.notifiedAt).toISOString() : "",
        reminderSentAt: r.reminderSentAt ? new Date(r.reminderSentAt).toISOString() : "",
        unsubscribedAt: r.unsubscribedAt ? new Date(r.unsubscribedAt).toISOString() : "",
      })),
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Launch Subscribers</h1>
          <p className="text-sm text-muted-foreground">
            {totals
              ? `${totals.pending.toLocaleString()} pending · ${totals.notified.toLocaleString()} notified · ${totals.unsubscribed.toLocaleString()} unsubscribed · ${totals.all.toLocaleString()} total`
              : "Loading…"}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
          <Download className="mr-2 h-4 w-4" /> Export CSV
        </Button>
      </div>

      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-4 md:grid-cols-4">
          <div className="space-y-1 md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">
              Search (email prefix)
            </label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
                placeholder="ada@"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Status</label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as (typeof STATUSES)[number])}
            >
              {STATUSES.map((s) => (
                <option key={s || "all"} value={s}>
                  {s || "All statuses"}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk action bar */}
      {selectedIds.length > 0 && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 p-4">
            <span className="text-sm font-medium">
              {selectedIds.length} selected
              {eligibleSelectedIds.length !== selectedIds.length && (
                <span className="ml-1 text-xs text-muted-foreground">
                  ({eligibleSelectedIds.length} pending — others will be skipped)
                </span>
              )}
            </span>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={handleSendLaunch}
                disabled={busy !== false || eligibleSelectedIds.length === 0}
              >
                {busy === "launch" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Send launch email
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReminderOpen((v) => !v)}
                disabled={busy !== false || eligibleSelectedIds.length === 0}
              >
                <Bell className="mr-2 h-4 w-4" />
                Send reminder
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleUnsubscribe}
                disabled={busy !== false}
                className="border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950"
              >
                {busy === "unsub" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <UserX className="mr-2 h-4 w-4" />
                )}
                Mark unsubscribed
              </Button>
            </div>
            {reminderOpen && (
              <div className="mt-2 flex w-full flex-col gap-2 sm:flex-row sm:items-end">
                <div className="flex-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    Optional message (max 600 chars)
                  </label>
                  <Input
                    value={reminderMsg}
                    onChange={(e) => setReminderMsg(e.target.value)}
                    maxLength={600}
                    placeholder="A short personal note appended to the reminder"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={handleSendReminder}
                  disabled={busy !== false || eligibleSelectedIds.length === 0}
                >
                  {busy === "reminder" ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  Send to {eligibleSelectedIds.length}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
      {info && (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300">
          {info}
        </div>
      )}

      <DataTable
        data={rows}
        columns={columns}
        isFetchingMore={query.isFetchingNextPage}
        hasMore={Boolean(query.hasNextPage)}
        onLoadMore={() => void query.fetchNextPage()}
        rowKey={(r) => r.id}
        rowSelection={selection}
        onRowSelectionChange={setSelection}
        {...queryTableProps(query)}
      />
    </div>
  );
}

function fmtDate(ms: number | null): React.ReactNode {
  if (!ms) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className="font-mono text-xs">{format(new Date(ms), "yyyy-MM-dd HH:mm")}</span>
  );
}
